import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Spinner, Button } from "reactstrap";

/**
 * The tabs of one Red Team engagement. They are defined here, outside the page component, so React
 * keeps them (and what people are typing) between renders. Everything they need comes in through ctx.
 */

export const STATUS_LABELS = {
  draft: "Draft",
  pending_approval: "Waiting for approval",
  approved: "Approved",
  active: "Active",
  completed: "Completed",
  stopped: "Stopped",
  cancelled: "Cancelled",
};
export const OUTCOME_LABELS = { success: "Success", partial: "Partial", failed: "Failed", blocked: "Blocked", not_attempted: "Not attempted" };
export const DETECTION_LABELS = { detected: "Detected", partial: "Partly detected", not_detected: "Not detected", unknown: "Not checked" };
export const ACTION_LABELS = { open: "Open", in_progress: "In progress", done: "Done", retest_needed: "Retest needed", verified: "Verified" };

export const fmt = (value, withTime = true) => {
  const d = new Date(value);
  if (!value || Number.isNaN(d.getTime())) return "—";
  return withTime
    ? d.toLocaleString([], { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString([], { day: "2-digit", month: "short", year: "numeric" });
};

export const Pill = ({ status }) => <span className={`rt-pill is-${status}`}>{STATUS_LABELS[status] || status}</span>;
export const DetectionBadge = ({ status }) => <span className={`rt-badge is-${status || "unknown"}`}>{DETECTION_LABELS[status] || "Not checked"}</span>;

const FOLLOW_UP_STATUSES = ["active", "completed", "stopped"];

export const ApprovalsTab = ({ ctx }) => {
  const { e, meta, canWrite, canApprove, busy, act, mine } = ctx;
  const [ask, setAsk] = useState({ category: "", justification: "" });
  const [notes, setNotes] = useState({});
  const open = ["draft", "pending_approval", "approved", "active"].includes(e.status);
  return (
    <div className="rt-stack">
      <div className="rt-note">
        Separate approvals are needed before these can be carried out or recorded: {Object.values(meta.sensitiveCategories).join(", ").toLowerCase()}. The person who asks cannot approve their own request. Destructive actions and denial of service are never allowed.
      </div>
      <div>
        <div className="drawer-section-title">Engagement approval</div>
        <div className="rt-row">
          <Pill status={e.status} />
          <span className="rt-muted">
            {e.approvedBy ? `Approved by ${e.approvedBy} on ${fmt(e.approvedAt)}${e.approvalNote ? `: ${e.approvalNote}` : ""}` : e.status === "pending_approval" ? `Asked by ${e.requestedBy} on ${fmt(e.submittedAt)}` : "Not approved yet"}
          </span>
        </div>
      </div>
      <div>
        <div className="drawer-section-title">Separate approvals</div>
        <div className="rt-list">
          {Object.entries(meta.sensitiveCategories).map(([key, label]) => {
            const a = (e.approvals || []).find((x) => x.category === key);
            return (
              <div className="rt-card" key={key}>
                <div className="rt-row">
                  <b>{label}</b>
                  <span className={`rt-badge is-${a ? a.status : "none"}`}>{a ? a.status : "Not requested"}</span>
                  {a && <span className="rt-muted">{a.status === "pending" ? `Asked by ${a.requestedBy}` : `${a.status === "approved" ? "Approved" : "Rejected"} by ${a.decidedBy} on ${fmt(a.decidedAt)}`}</span>}
                </div>
                {a && a.justification && <div className="rt-muted">Reason: {a.justification}</div>}
                {a && a.note && <div className="rt-muted">Note: {a.note}</div>}
                {a && a.status === "pending" && canApprove && (
                  <div className="rt-inline">
                    <input placeholder="Note (required to reject)" value={notes[key] || ""} onChange={(ev) => setNotes({ ...notes, [key]: ev.target.value })} />
                    <Button color="primary" size="sm" disabled={busy} onClick={() => act(`/engagements/${e._id}/approvals/${key}/decision`, { method: "POST", body: { decision: "approve", note: notes[key] } }, `${label} approved.`)}>Approve</Button>
                    <Button color="danger" outline size="sm" disabled={busy} onClick={() => act(`/engagements/${e._id}/approvals/${key}/decision`, { method: "POST", body: { decision: "reject", note: notes[key] } }, `${label} rejected.`)}>Reject</Button>
                  </div>
                )}
                {a && a.status === "pending" && canApprove && mine(a.requestedBy) && !meta.selfApprovalAllowed && <div className="rt-muted">You asked for this yourself, so someone else has to approve it.</div>}
              </div>
            );
          })}
        </div>
      </div>
      {canWrite && open && (
        <div className="mgmt-box">
          <div className="drawer-section-title">Ask for a separate approval</div>
          <div className="rt-inline">
            <select value={ask.category} onChange={(ev) => setAsk({ ...ask, category: ev.target.value })}>
              <option value="">Choose…</option>
              {Object.entries(meta.sensitiveCategories).map(([k, label]) => (<option key={k} value={k}>{label}</option>))}
            </select>
            <input placeholder="Why is it needed?" value={ask.justification} onChange={(ev) => setAsk({ ...ask, justification: ev.target.value })} />
            <Button color="primary" size="sm" disabled={busy || !ask.category} onClick={() => act(`/engagements/${e._id}/approvals`, { method: "POST", body: ask }, "Approval requested.", () => setAsk({ category: "", justification: "" }))}>Request</Button>
          </div>
        </div>
      )}
    </div>
  );
};

const BLANK_ACTIVITY = { action: "", target: "", technique: "", customId: "", customName: "", tactic: "", outcome: "success", businessImpact: "", evidence: "", at: "" };

export const ActivityTab = ({ ctx }) => {
  const { e, meta, canWrite, busy, setBusy, call, say, loadTab, activities } = ctx;
  const [form, setForm] = useState(BLANK_ACTIVITY);
  const [formError, setFormError] = useState("");
  const [openId, setOpenId] = useState(null);
  const [det, setDet] = useState({ status: "unknown", source: "", alertRef: "", notes: "", evidence: "" });
  const custom = form.technique === "__custom";

  const submit = async () => {
    setFormError("");
    const body = { action: form.action, target: form.target, outcome: form.outcome, businessImpact: form.businessImpact };
    if (form.at) body.at = new Date(form.at).toISOString();
    if (custom) Object.assign(body, { techniqueId: form.customId, techniqueName: form.customName, tactic: form.tactic });
    else if (form.technique) body.techniqueId = form.technique;
    if (form.evidence.trim()) body.evidence = [{ kind: "note", text: form.evidence }];
    setBusy(true);
    const r = await call(`/engagements/${e._id}/activities`, { method: "POST", body });
    setBusy(false);
    if (!r.ok) return setFormError(r.body.error || "That activity was refused.");
    say("success", "Activity recorded.");
    setForm(BLANK_ACTIVITY);
    loadTab(e._id, "activity");
  };

  const openRow = (a) => {
    setOpenId(openId === a._id ? null : a._id);
    setDet({ status: (a.detection && a.detection.status) || "unknown", source: (a.detection && a.detection.source) || "", alertRef: (a.detection && a.detection.alertRef) || "", notes: (a.detection && a.detection.notes) || "", evidence: "" });
  };

  const saveDetection = async (a) => {
    const body = { detection: { status: det.status, source: det.source, alertRef: det.alertRef, notes: det.notes } };
    if (det.evidence.trim()) body.evidence = { kind: "note", text: det.evidence };
    setBusy(true);
    const r = await call(`/engagements/${e._id}/activities/${a._id}`, { method: "PATCH", body });
    setBusy(false);
    if (!r.ok) return say("danger", r.body.error || "Could not save.");
    say("success", "Detection result saved.");
    loadTab(e._id, "activity");
  };

  const followUp = FOLLOW_UP_STATUSES.includes(e.status);
  return (
    <div className="rt-stack">
      {e.status === "active" && canWrite && (
        <div className="mgmt-box">
          <div className="drawer-section-title">Record an activity</div>
          <div className="rt-note">The plan decides what is allowed: the target must be in scope and not excluded, the time must be inside the test window, and anything sensitive needs its approval first.</div>
          {formError && <div className="rt-note is-error">{formError}</div>}
          <div className="rt-form">
            <label>What was done<textarea rows={2} value={form.action} onChange={(ev) => setForm({ ...form, action: ev.target.value })} /></label>
            <div className="rt-two">
              <label>Target (host name or IP)<input value={form.target} onChange={(ev) => setForm({ ...form, target: ev.target.value })} placeholder="app.example.com" /></label>
              <label>
                MITRE ATT&CK technique
                <select value={form.technique} onChange={(ev) => setForm({ ...form, technique: ev.target.value })}>
                  <option value="">Not mapped</option>
                  {meta.tactics.map((t) => (
                    <optgroup key={t.shortname} label={t.name + (t.shortname === "impact" ? " (never allowed)" : "")}>
                      {meta.techniques.filter((x) => x.tactic === t.shortname).map((x) => (<option key={x.id} value={x.id} disabled={t.shortname === "impact"}>{x.id} {x.name}</option>))}
                    </optgroup>
                  ))}
                  <option value="__custom">Other technique…</option>
                </select>
              </label>
            </div>
            {custom && (
              <div className="rt-two">
                <label>Technique id (like T1190)<input value={form.customId} onChange={(ev) => setForm({ ...form, customId: ev.target.value })} /></label>
                <label>Technique name<input value={form.customName} onChange={(ev) => setForm({ ...form, customName: ev.target.value })} /></label>
                <label>
                  Tactic
                  <select value={form.tactic} onChange={(ev) => setForm({ ...form, tactic: ev.target.value })}>
                    <option value="">Choose…</option>
                    {meta.tactics.map((t) => (<option key={t.shortname} value={t.shortname}>{t.name}</option>))}
                  </select>
                </label>
              </div>
            )}
            <div className="rt-two">
              <label>Result<select value={form.outcome} onChange={(ev) => setForm({ ...form, outcome: ev.target.value })}>{meta.outcomes.map((o) => (<option key={o} value={o}>{OUTCOME_LABELS[o]}</option>))}</select></label>
              <label>Time (leave empty for now)<input type="datetime-local" value={form.at} onChange={(ev) => setForm({ ...form, at: ev.target.value })} /></label>
            </div>
            <label>Business impact<input value={form.businessImpact} onChange={(ev) => setForm({ ...form, businessImpact: ev.target.value })} placeholder="What would this mean for the business if a real attacker did it?" /></label>
            <label>Evidence (a note, a link, or where the proof is stored)<input value={form.evidence} onChange={(ev) => setForm({ ...form, evidence: ev.target.value })} /></label>
            <div className="mgmt-actions"><Button color="primary" size="sm" disabled={busy || !form.action.trim() || !form.target.trim()} onClick={submit}>{busy ? <Spinner size="sm" /> : "Record activity"}</Button></div>
          </div>
        </div>
      )}
      <div className="findings-table-wrap">
        <table className="findings-table">
          <thead><tr><th style={{ width: 150 }}>Time</th><th style={{ width: 150 }}>Operator</th><th style={{ width: 200 }}>Technique</th><th style={{ width: 160 }}>Target</th><th>What was done</th><th style={{ width: 110 }}>Result</th><th style={{ width: 140 }}>Detection</th></tr></thead>
          <tbody>
            {activities.length === 0 ? (
              <tr><td colSpan="7" style={{ textAlign: "center", padding: 30, color: "var(--fg-tertiary)" }}>{e.status === "active" ? "Nothing recorded yet." : "No activity was recorded."}</td></tr>
            ) : (
              activities.map((a) => (
                <React.Fragment key={a._id}>
                  <tr onClick={() => openRow(a)} className={openId === a._id ? "selected-row" : ""} style={{ cursor: "pointer" }}>
                    <td>{fmt(a.at)}</td>
                    <td className="rt-small">{a.operator}</td>
                    <td>{a.techniqueId ? `${a.techniqueId} ${a.techniqueName || ""}` : <span className="rt-muted">Not mapped</span>}{a.sensitiveCategory && <span className="rt-badge is-sensitive">{meta.sensitiveCategories[a.sensitiveCategory]}</span>}</td>
                    <td className="rt-small">{a.target}</td>
                    <td>{a.action}</td>
                    <td>{OUTCOME_LABELS[a.outcome] || a.outcome}</td>
                    <td><DetectionBadge status={a.detection && a.detection.status} /></td>
                  </tr>
                  {openId === a._id && (
                    <tr className="rt-expand"><td colSpan="7">
                      <div className="rt-stack">
                        {a.businessImpact && <div><b>Business impact:</b> {a.businessImpact}</div>}
                        {(a.evidence || []).length > 0 && <div><b>Evidence:</b> {a.evidence.map((x, i) => (<div className="rt-muted" key={i}>• {x.text} ({x.by}, {fmt(x.at)})</div>))}</div>}
                        {a.detection && a.detection.updatedBy && a.detection.status !== "unknown" && <div className="rt-muted">Detection last updated by {a.detection.updatedBy} on {fmt(a.detection.updatedAt)}{a.detection.source ? ` · ${a.detection.source}` : ""}{a.detection.notes ? ` · ${a.detection.notes}` : ""}</div>}
                        {followUp && canWrite && (
                          <div className="mgmt-box">
                            <div className="drawer-section-title">Did the defenders see it? (purple-team follow-up)</div>
                            <div className="rt-form">
                              <div className="rt-two">
                                <label>Detection<select value={det.status} onChange={(ev) => setDet({ ...det, status: ev.target.value })}>{meta.detectionStatuses.map((s) => (<option key={s} value={s}>{DETECTION_LABELS[s]}</option>))}</select></label>
                                <label>Seen by (SIEM, EDR…)<input value={det.source} onChange={(ev) => setDet({ ...det, source: ev.target.value })} /></label>
                              </div>
                              <div className="rt-two">
                                <label>Alert reference<input value={det.alertRef} onChange={(ev) => setDet({ ...det, alertRef: ev.target.value })} /></label>
                                <label>Notes<input value={det.notes} onChange={(ev) => setDet({ ...det, notes: ev.target.value })} /></label>
                              </div>
                              <label>Add evidence<input value={det.evidence} onChange={(ev) => setDet({ ...det, evidence: ev.target.value })} /></label>
                              <div className="mgmt-actions"><Button color="primary" size="sm" disabled={busy} onClick={() => saveDetection(a)}>Save</Button></div>
                            </div>
                          </div>
                        )}
                      </div>
                    </td></tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="rt-muted">The log cannot be edited or deleted. Detection results and extra evidence can be added later, and each change is recorded.</div>
    </div>
  );
};

const BLANK_ACTION = { title: "", description: "", priority: "medium", owner: "", dueDate: "", activityId: "" };

export const GapsTab = ({ ctx }) => {
  const { e, meta, canWrite, busy, setBusy, call, say, loadTab, activities, actions } = ctx;
  const [form, setForm] = useState(BLANK_ACTION);
  const gaps = activities.filter((a) => a.detection && ["not_detected", "partial"].includes(a.detection.status));

  const create = async () => {
    setBusy(true);
    const r = await call(`/engagements/${e._id}/actions`, { method: "POST", body: { ...form, activityId: form.activityId || undefined } });
    setBusy(false);
    if (!r.ok) return say("danger", r.body.error || "Could not create the action.");
    say("success", "Improvement action created.");
    setForm(BLANK_ACTION);
    loadTab(e._id, "gaps");
  };
  const update = async (x, body) => {
    const r = await call(`/engagements/${e._id}/actions/${x._id}`, { method: "PATCH", body });
    if (!r.ok) return say("danger", r.body.error || "Could not update.");
    loadTab(e._id, "gaps");
  };

  return (
    <div className="rt-stack">
      <div>
        <div className="drawer-section-title">Detection gaps ({gaps.length})</div>
        {gaps.length === 0 ? <div className="rt-muted">No gaps recorded. A gap is an activity the defenders did not fully detect.</div> : (
          <div className="rt-list">
            {gaps.map((a) => (
              <div className="rt-card" key={a._id}>
                <div className="rt-row"><b>{a.techniqueId ? `${a.techniqueId} ${a.techniqueName || ""}` : "Unmapped technique"}</b><span className="rt-muted">on {a.target}</span><DetectionBadge status={a.detection.status} /></div>
                <div className="rt-muted">{a.action}</div>
                {canWrite && <button type="button" className="rt-link" onClick={() => setForm({ ...BLANK_ACTION, activityId: a._id, title: `Detect ${a.techniqueId || "this activity"} on ${a.target}`, priority: a.detection.status === "not_detected" ? "high" : "medium" })}>Create an improvement action for this</button>}
              </div>
            ))}
          </div>
        )}
      </div>
      <div>
        <div className="drawer-section-title">Improvement actions ({actions.length})</div>
        {actions.length === 0 ? <div className="rt-muted">None yet.</div> : (
          <div className="findings-table-wrap">
            <table className="findings-table">
              <thead><tr><th>Action</th><th style={{ width: 90 }}>Priority</th><th style={{ width: 160 }}>Status</th><th style={{ width: 200 }}>Owner</th><th style={{ width: 110 }}>Due</th></tr></thead>
              <tbody>
                {actions.map((x) => (
                  <tr key={x._id}>
                    <td><b>{x.title}</b>{x.description && <div className="rt-muted">{x.description}</div>}</td>
                    <td><span className={`rt-badge is-prio-${x.priority}`}>{x.priority}</span></td>
                    <td>{canWrite ? (<select value={x.status} onChange={(ev) => update(x, { status: ev.target.value })}>{meta.actionStatuses.map((s) => (<option key={s} value={s}>{ACTION_LABELS[s]}</option>))}</select>) : ACTION_LABELS[x.status]}</td>
                    <td className="rt-small">{x.owner || "Unassigned"}</td>
                    <td>{x.dueDate ? fmt(x.dueDate, false) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {canWrite && (
        <div className="mgmt-box">
          <div className="drawer-section-title">New improvement action</div>
          <div className="rt-form">
            <label>Title<input value={form.title} onChange={(ev) => setForm({ ...form, title: ev.target.value })} placeholder="Add a SIEM rule for public-exploit attempts" /></label>
            <label>Details<input value={form.description} onChange={(ev) => setForm({ ...form, description: ev.target.value })} /></label>
            <div className="rt-two">
              <label>Priority<select value={form.priority} onChange={(ev) => setForm({ ...form, priority: ev.target.value })}>{meta.priorities.map((p) => (<option key={p} value={p}>{p}</option>))}</select></label>
              <label>Owner (a user of your organization)<input type="email" value={form.owner} onChange={(ev) => setForm({ ...form, owner: ev.target.value })} /></label>
            </div>
            <div className="rt-two">
              <label>Due date<input type="date" value={form.dueDate} onChange={(ev) => setForm({ ...form, dueDate: ev.target.value })} /></label>
              <label>From activity<select value={form.activityId} onChange={(ev) => setForm({ ...form, activityId: ev.target.value })}><option value="">None</option>{gaps.map((a) => (<option key={a._id} value={a._id}>{a.techniqueId || "Activity"} on {a.target}</option>))}</select></label>
            </div>
            <div className="mgmt-actions"><Button color="primary" size="sm" disabled={busy || !form.title.trim()} onClick={create}>Create action</Button></div>
          </div>
        </div>
      )}
    </div>
  );
};

export const FindingsTab = ({ ctx }) => {
  const { findings } = ctx;
  return (
    <div className="rt-stack">
      <div className="rt-note">Open findings from your attack-surface scans on assets inside this engagement's scope. They show where a realistic attack path may start.</div>
      <div className="findings-table-wrap">
        <table className="findings-table">
          <thead><tr><th style={{ width: 120 }}>Severity</th><th>Finding</th><th style={{ width: 220 }}>Asset</th><th style={{ width: 130 }}>Source</th></tr></thead>
          <tbody>
            {findings.length === 0 ? (
              <tr><td colSpan="4" style={{ textAlign: "center", padding: 30, color: "var(--fg-tertiary)" }}>No open findings on assets in scope.</td></tr>
            ) : (
              findings.map((f) => (
                <tr key={f.id}>
                  <td><span className={`sev-badge sev-${f.severity}`}><span className="dot" />{f.severity}</span></td>
                  <td>{f.title}</td>
                  <td className="rt-small">{f.assetId}</td>
                  <td><span className="module-tag">{f.module}</span></td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <Link to="/findings" className="rt-link">Open the Findings page →</Link>
    </div>
  );
};

export const ReportTab = ({ ctx }) => {
  const { e, canWrite, busy, act, say, loadTab, report, lessons, setLessons, apiBase, token } = ctx;
  const [downloading, setDownloading] = useState("");
  const canEditLessons = canWrite && FOLLOW_UP_STATUSES.includes(e.status);

  const download = async (format) => {
    setDownloading(format);
    try {
      const res = await fetch(`${apiBase}redteam/engagements/${e._id}/report?format=${format}${format === "json" ? "&download=1" : ""}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || "Download failed");
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `RedTeam_${e.name.replace(/[^a-zA-Z0-9]+/g, "_")}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      say("danger", err.message);
    } finally {
      setDownloading("");
    }
  };

  const n = report && report.executiveSummary.keyNumbers;
  return (
    <div className="rt-stack">
      <div className="rt-inline">
        <Button color="primary" size="sm" disabled={!!downloading} onClick={() => download("pdf")}>{downloading === "pdf" ? <Spinner size="sm" /> : "Download PDF"}</Button>
        <Button color="primary" outline size="sm" disabled={!!downloading} onClick={() => download("html")}>HTML</Button>
        <Button color="primary" outline size="sm" disabled={!!downloading} onClick={() => download("json")}>JSON</Button>
      </div>
      {!report ? <div style={{ textAlign: "center", padding: 20 }}><Spinner color="primary" /></div> : (
        <>
          <div className="rt-numbers">
            <div className="rt-num"><b>{n.activities}</b>activities</div>
            <div className="rt-num"><b>{n.detectionRate === null ? "n/a" : `${n.detectionRate}%`}</b>detection rate</div>
            <div className="rt-num"><b>{n.gaps}</b>detection gaps</div>
            <div className="rt-num"><b>{n.openActions}</b>open actions</div>
            <div className="rt-num"><b>{n.correlatedFindings}</b>findings in scope</div>
          </div>
          <div><div className="drawer-section-title">Executive summary</div>{report.executiveSummary.paragraphs.map((p, i) => (<p key={i} className="rt-para">{p}</p>))}</div>
        </>
      )}
      <div>
        <div className="drawer-section-title">Lessons learned</div>
        <textarea className="rt-text" rows={4} value={lessons} disabled={!canEditLessons} onChange={(ev) => setLessons(ev.target.value)} placeholder={FOLLOW_UP_STATUSES.includes(e.status) ? "What worked, what did not, what to change next time." : "Available once the exercise has started."} />
        {canEditLessons && (
          <div className="mgmt-actions"><Button color="primary" size="sm" disabled={busy} onClick={() => act(`/engagements/${e._id}/lessons`, { method: "PUT", body: { lessonsLearned: lessons } }, "Lessons learned saved.", () => loadTab(e._id, "report"))}>Save</Button></div>
        )}
      </div>
    </div>
  );
};
